<script setup>
import {computed, inject, ref} from "vue";
import IconCopy from "@/assets/icons/IconCopy.vue";
import ButtonNeutralTertiary from "@/components/Buttons/Default/ButtonNeutralTertiary.vue";


const notify = inject("$notify");

const props = defineProps({
  code: {
    type: String,
    required: true
  }
});


// Функция подсветки синтаксиса Vue
const highlightVueCode = (code) => {
  if (!code) return "";

  // Экранируем HTML символы
  let highlighted = code
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");

  // Парсинг аттрибутов
  const parseAttributes = (attrString) => {
    if (!attrString.trim()) return "";

    const attrRegex = /([\s\n]+)([a-zA-Z@:][a-zA-Z0-9-_:]*)(=?)("([^"]*)"|'([^']*)'|([\S]+))?/g;
    let result = "";
    let lastIndex = 0;
    let match;
    
    while ((match = attrRegex.exec(attrString)) !== null) {
      // Добавляем текст до совпадения
      result += attrString.slice(lastIndex, match.index);
      
      const [, spaces, attrName, equals, , doubleQuoted, singleQuoted, unquoted] = match;
      
      // Добавляем пробелы перед атрибутом
      result += spaces;
      
      // Определяем тип атрибута
      const isVueDirective = /^(v-|@|:)/.test(attrName);
      const attrClass = isVueDirective ? "code-example__vue-directive" : "code-example__attribute";
      
      // Добавляем имя атрибута с подсветкой
      result += `<span class="${attrClass}">${attrName}</span>`;
      
      // Если есть знак равенства и значение
      if (equals && (doubleQuoted !== undefined || singleQuoted !== undefined || unquoted !== undefined)) {
        result += equals;
        
        if (doubleQuoted !== undefined) {
          result += `"<span class="code-example__string">${doubleQuoted}</span>"`;
        } else if (singleQuoted !== undefined) {
          result += `'<span class="code-example__string">${singleQuoted}</span>'`;
        } else if (unquoted !== undefined) {
          result += `<span class="code-example__string">${unquoted}</span>`;
        }
      }
      
      lastIndex = attrRegex.lastIndex;
    }
    
    // Добавляем оставшийся текст
    result += attrString.slice(lastIndex);
    
    return result;
  };

  // Подсветка тегов и атрибутов
  highlighted = highlighted.replace(
      /&lt;(\/?[a-zA-Z][a-zA-Z0-9-]*)([\s\S]*?)&gt;/g,
      (match, tagName, attributes) => {
        const processedAttrs = parseAttributes(attributes);
        return `&lt;<span class="code-example__tag">${tagName}</span>${processedAttrs}&gt;`;
      }
  );

  // Подсветка комментариев HTML
  highlighted = highlighted.replace(
      /(&lt;!--[\s\S]*?--&gt;)/g,
      "<span class=\"code-example__comment\">$1</span>"
  );

  return highlighted;
};

// Обработанный код с подсветкой
const highlightedCode = computed(() => {
  return highlightVueCode(props.code);
});

// Состояние для кнопки копирования
const isCopied = ref(false);

// Функция копирования кода
const copyCode = async () => {
  try {
    await navigator.clipboard.writeText(props.code);
    isCopied.value = true;
    notify.success("Код скопирован в буфер обмена");
    setTimeout(() => {
      isCopied.value = false;
    }, 2000);
  } catch (err) {
    notify.error("Ошибка копирования кода в буфер обмена");
    console.error("Ошибка при копировании:", err);
  }
};
</script>

<template>
  <div class="code-container">
    <code
      class="code-example"
      v-html="highlightedCode"
    />
    <ButtonNeutralTertiary
      class="copy-button"
      :pressed="isCopied"
      size="small"
      @click="copyCode"
    >
      <IconCopy />
      <span class="copy-text">{{ isCopied ? 'Скопировано!' : 'Копировать' }}</span>
    </ButtonNeutralTertiary>
  </div>
</template>

<style scoped lang="scss">
.code-container {
  position: relative;
  border-radius: 8px;
  border: 1px solid #e1e5e9;
  overflow: hidden;

  &:hover .copy-button {
    opacity: 1;
  }
}

.code-example {
  background-color: var(--white);
  padding: 10px 50px 10px 10px;
  font-size: 14px;
  white-space: break-spaces;
  font-family: 'Consolas', 'Monaco', 'Courier New', monospace;
  display: block;
  margin: 0;
  width: 100%;
  box-sizing: border-box;
  min-height: 45px;

  // Стили подсветки синтаксиса Vue
  :deep(.code-example__tag) {
    color: #276513; // Красный для тегов
    font-weight: 600;
  }

  :deep(.code-example__attribute) {
    color: #3498db; // Атрибуты
    font-weight: 600;
  }

  :deep(.code-example__string) {
    color: #000000; // Строки
  }

  :deep(.code-example__vue-directive) {
    color: #3498db; // Директивы
    font-weight: 600;
  }

  :deep(.code-example__comment) {
    color: #95a5a6; // Коменты
    font-style: italic;
  }
}

.copy-button {
  position: absolute;
  top: 8px;
  right: 8px;
  opacity: 0;
  transition: opacity 0.3s ease-in-out;
}
</style>